import React, { useEffect, useState } from 'react';
import { getSession, getCurrentUser } from '../../services/authUtils';
import { getOrCreateUser } from '../../services/supabase';

/**
 * OAuth Callback Handler
 * Handles OAuth redirects from Supabase
 */
const AuthCallback = ({ onAuthSuccess }) => {
  const [status, setStatus] = useState('Processing...');
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Get session from URL hash (Supabase OAuth redirects use hash)
        const session = await getSession();
        
        if (session) {
          // User is authenticated
          const user = await getCurrentUser();
          
          if (user) {
            // Ensure user exists in users table
            await getOrCreateUser(user.id);
            
            // Set cookie for server-side auth check
            // Use domain-less cookie so it works for all subpaths
            if (session.access_token) {
              try {
                const expires = new Date(session.expires_at * 1000).toUTCString();
                const cookieValue = `sb-access-token=${session.access_token}; expires=${expires}; path=/; SameSite=Lax; Secure`;
                document.cookie = cookieValue;
                console.log('[AUTH CALLBACK] Cookie set');
              } catch (cookieError) {
                console.error('[AUTH CALLBACK] Error setting cookie:', cookieError);
              }
            }
            
            // Optionally create user in token service
            try {
              const response = await fetch('https://diet-project-v1.onrender.com/api/auth/signup', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  email: user.email,
                  name: user.user_metadata?.name || user.email?.split('@')[0]
                })
              });
              
              if (!response.ok) {
                console.warn('Failed to create user in token service, but Supabase user created');
              }
            } catch (apiError) {
              console.warn('Error creating user in token service:', apiError);
              // Don't fail auth if token service call fails
            }
            
            setStatus('Authentication successful!');
            
            // Get return URL from query params
            const urlParams = new URLSearchParams(window.location.search);
            const returnUrl = urlParams.get('return') || '/';
            
            // Call success callback
            if (onAuthSuccess) {
              onAuthSuccess();
            } else {
              // Redirect after a short delay
              setTimeout(() => {
                window.location.href = returnUrl;
              }, 1000);
            }
          } else {
            throw new Error('Failed to get user information');
          }
        } else {
          throw new Error('No session found');
        }
      } catch (err) {
        console.error('OAuth callback error:', err);
        setError(err.message || 'Authentication failed');
        setStatus('Authentication failed');
        
        // Redirect to login after error
        setTimeout(() => {
          const urlParams = new URLSearchParams(window.location.search);
          const returnUrl = urlParams.get('return') || '/';
          window.location.href = `/login?return=${encodeURIComponent(returnUrl)}&error=oauth_failed`;
        }, 2000);
      }
    };

    handleCallback();
  }, [onAuthSuccess]);

  return (
    <div className="min-h-screen bg-paper flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
        <p className="text-gray-600">{status}</p>
        {error && (
          <p className="text-red-600 mt-2">{error}</p>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
